import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import {
  Plus,
  Search,
  Filter,
  MoreVertical,
  Check,
  X,
  FileText,
  Calendar,
  Users,
  AlertTriangle,
  Clock,
  History,
  Settings
} from 'lucide-react';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { formatDate, formatDateShort, getStatusColor, getStatusLabel } from '../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Interviews = () => {
  const [interviews, setInterviews] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [interviewTypes, setInterviewTypes] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showAlerts, setShowAlerts] = useState(true);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [historyData, setHistoryData] = useState([]);

  const [formData, setFormData] = useState({
    employee_id: '',
    type_entretien: '',
    date_entretien: '',
    heure_entretien: '',
    duree_minutes: 60,
    lieu: '',
    evaluateur: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [interviewsRes, employeesRes, typesRes, alertsRes] = await Promise.all([
        axios.get(`${API_URL}/interviews`),
        axios.get(`${API_URL}/employees`),
        axios.get(`${API_URL}/admin/interview-types`).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/interviews/alerts`).catch(() => ({ data: [] }))
      ]);
      setInterviews(interviewsRes.data);
      setEmployees(employeesRes.data);
      setInterviewTypes(typesRes.data || {});
      setAlerts(alertsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async (employeeId, typeEntretien = null) => {
    try {
      let url = `${API_URL}/interviews/history/${employeeId}`;
      if (typeEntretien) {
        url += `?type_entretien=${typeEntretien}`;
      }
      const response = await axios.get(url);
      setHistoryData(response.data);
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Erreur lors du chargement de l\'historique');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/interviews`, formData);
      toast.success('Entretien planifié');
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error creating interview:', error);
      toast.error('Erreur lors de la création');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${API_URL}/interviews/${id}/status?statut=${newStatus}`);
      toast.success(`Entretien marqué comme ${newStatus === 'realise' ? 'réalisé' : 'annulé'}`);
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: '',
      type_entretien: '',
      date_entretien: '',
      heure_entretien: '',
      duree_minutes: 60,
      lieu: '',
      evaluateur: '',
      notes: ''
    });
  };

  // Get type label from config
  const getTypeLabel = (typeCode) => {
    if (interviewTypes[typeCode]) {
      return interviewTypes[typeCode].label;
    }
    // Fallback to static list
    const staticTypes = {
      'annuel': 'Entretien annuel',
      'professionnel': 'Entretien professionnel',
      'fin_periode_essai': 'Fin période d\'essai',
      'retour_absence': 'Entretien de reprise',
      'recadrage': 'Entretien de recadrage'
    };
    return staticTypes[typeCode] || typeCode;
  };

  // Get urgency badge
  const getUrgencyBadge = (urgence) => {
    switch (urgence) {
      case 'critique':
        return <Badge className="bg-red-100 text-red-800">En retard</Badge>;
      case 'urgent':
        return <Badge className="bg-orange-100 text-orange-800">Urgent</Badge>;
      default:
        return <Badge className="bg-yellow-100 text-yellow-800">À planifier</Badge>;
    }
  };

  const filteredInterviews = interviews.filter(int => {
    const matchesSearch = 
      int.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      int.type_entretien.toLowerCase().includes(searchTerm.toLowerCase()) ||
      int.evaluateur.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || int.statut === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  // Convert interviewTypes object to array for select
  const interviewTypesArray = Object.entries(interviewTypes).map(([code, config]) => ({
    value: code,
    label: config.label || code,
    duree_default: config.duree_default || 60
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F5156]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="interviews-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Entretiens</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Planifiez et suivez les entretiens professionnels
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#0F5156] hover:bg-[#0d464a]" data-testid="add-interview-btn">
              <Plus className="w-4 h-4 mr-2" />
              Planifier un entretien
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Nouvel entretien</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="employee">Employé *</Label>
                <Select 
                  value={formData.employee_id} 
                  onValueChange={(value) => setFormData({ ...formData, employee_id: value })}
                >
                  <SelectTrigger data-testid="interview-employee-select">
                    <SelectValue placeholder="Sélectionner un employé" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.prenom} {emp.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">Type d'entretien *</Label>
                <Select
                  value={formData.type_entretien}
                  onValueChange={(value) => {
                    const typeConfig = interviewTypes[value];
                    setFormData({
                      ...formData,
                      type_entretien: value,
                      duree_minutes: typeConfig?.duree_default || 60
                    });
                  }}
                >
                  <SelectTrigger data-testid="interview-type-select">
                    <SelectValue placeholder="Sélectionner un type" />
                  </SelectTrigger>
                  <SelectContent>
                    {interviewTypesArray.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date_entretien">Date *</Label>
                  <Input
                    id="date_entretien"
                    type="date"
                    value={formData.date_entretien}
                    onChange={(e) => setFormData({ ...formData, date_entretien: e.target.value })}
                    required
                    data-testid="interview-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="heure_entretien">Heure</Label>
                  <Input
                    id="heure_entretien"
                    type="time"
                    value={formData.heure_entretien}
                    onChange={(e) => setFormData({ ...formData, heure_entretien: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="duree_minutes">Durée (min)</Label>
                  <Select
                    value={formData.duree_minutes.toString()}
                    onValueChange={(value) => setFormData({ ...formData, duree_minutes: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 min</SelectItem>
                      <SelectItem value="45">45 min</SelectItem>
                      <SelectItem value="60">1h</SelectItem>
                      <SelectItem value="90">1h30</SelectItem>
                      <SelectItem value="120">2h</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lieu">Lieu</Label>
                  <Input
                    id="lieu"
                    value={formData.lieu}
                    onChange={(e) => setFormData({ ...formData, lieu: e.target.value })}
                    placeholder="Bureau, Salle réunion..."
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="evaluateur">Évaluateur *</Label>
                <Input
                  id="evaluateur"
                  value={formData.evaluateur}
                  onChange={(e) => setFormData({ ...formData, evaluateur: e.target.value })}
                  placeholder="Nom de l'évaluateur"
                  required
                  data-testid="interview-evaluator-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Notes préparatoires..."
                  rows={3}
                  data-testid="interview-notes-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" className="bg-[#0F5156] hover:bg-[#0d464a]" data-testid="interview-submit-btn">
                  Planifier
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="card-stat">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Planifiés</p>
                <p className="text-2xl font-bold text-amber-500">
                  {interviews.filter(i => i.statut === 'planifie').length}
                </p>
              </div>
              <Calendar className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-stat">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Réalisés</p>
                <p className="text-2xl font-bold text-emerald-500">
                  {interviews.filter(i => i.statut === 'realise').length}
                </p>
              </div>
              <Check className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-stat">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total</p>
                <p className="text-2xl font-bold text-[#0F5156]">
                  {interviews.length}
                </p>
              </div>
              <Users className="w-8 h-8 text-[#0F5156]" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-stat cursor-pointer hover:border-orange-300" onClick={() => setShowAlerts(!showAlerts)}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Alertes</p>
                <p className="text-2xl font-bold text-orange-500">
                  {alerts.length}
                </p>
              </div>
              <AlertTriangle className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts Section */}
      {showAlerts && alerts.length > 0 && (
        <Card className="border-orange-200 bg-orange-50 dark:bg-orange-900/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-orange-800 dark:text-orange-200">
              <AlertTriangle className="w-5 h-5" />
              Entretiens à planifier ({alerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {alerts.slice(0, 10).map((alert, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg border"
                >
                  <div className="flex-1">
                    <p className="font-medium">{alert.employee_name}</p>
                    <p className="text-sm text-slate-500">
                      {alert.type_label}
                      {alert.obligatoire && <span className="text-red-600 ml-1">*</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-sm text-slate-500">
                        {alert.jours_restants < 0
                          ? `${Math.abs(alert.jours_restants)}j de retard`
                          : `Dans ${alert.jours_restants}j`}
                      </p>
                      {getUrgencyBadge(alert.urgence)}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        setFormData({
                          ...formData,
                          employee_id: alert.employee_id,
                          type_entretien: alert.type_entretien,
                          date_entretien: alert.date_prevue,
                          duree_minutes: interviewTypes[alert.type_entretien]?.duree_default || 60
                        });
                        setIsDialogOpen(true);
                      }}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher par employé, type, évaluateur..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="interview-search-input"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-40" data-testid="interview-filter-status">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="planifie">Planifiés</SelectItem>
                <SelectItem value="realise">Réalisés</SelectItem>
                <SelectItem value="annule">Annulés</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Interviews Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="table-header">Employé</TableHead>
                  <TableHead className="table-header">Type</TableHead>
                  <TableHead className="table-header hidden md:table-cell">Date / Heure</TableHead>
                  <TableHead className="table-header hidden lg:table-cell">Durée</TableHead>
                  <TableHead className="table-header hidden lg:table-cell">Évaluateur</TableHead>
                  <TableHead className="table-header">Statut</TableHead>
                  <TableHead className="table-header text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInterviews.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      Aucun entretien trouvé
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredInterviews.map((interview) => (
                    <TableRow key={interview.id} className="table-row" data-testid={`interview-row-${interview.id}`}>
                      <TableCell className="table-cell">
                        <p className="font-medium">{interview.employee_name}</p>
                        {interview.lieu && <p className="text-xs text-slate-500">{interview.lieu}</p>}
                      </TableCell>
                      <TableCell className="table-cell">
                        {getTypeLabel(interview.type_entretien)}
                      </TableCell>
                      <TableCell className="table-cell hidden md:table-cell">
                        <div>
                          <p>{formatDateShort(interview.date_entretien)}</p>
                          {interview.heure_entretien && (
                            <p className="text-xs text-slate-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {interview.heure_entretien}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="table-cell hidden lg:table-cell">
                        {interview.duree_minutes ? `${interview.duree_minutes} min` : '-'}
                      </TableCell>
                      <TableCell className="table-cell hidden lg:table-cell">
                        {interview.evaluateur}
                      </TableCell>
                      <TableCell className="table-cell">
                        <Badge className={getStatusColor(interview.statut)}>
                          {getStatusLabel(interview.statut)}
                        </Badge>
                      </TableCell>
                      <TableCell className="table-cell text-right">
                        <div className="flex items-center justify-end gap-2">
                          {interview.statut === 'planifie' && (
                            <>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-emerald-600 hover:bg-emerald-50"
                                onClick={() => handleStatusChange(interview.id, 'realise')}
                                data-testid={`complete-interview-${interview.id}`}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => handleStatusChange(interview.id, 'annule')}
                                data-testid={`cancel-interview-${interview.id}`}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`interview-actions-${interview.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem>
                                <FileText className="w-4 h-4 mr-2" />
                                Voir détails
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => {
                                setSelectedHistory(interview);
                                fetchHistory(interview.employee_id, interview.type_entretien);
                              }}>
                                <History className="w-4 h-4 mr-2" />
                                Historique ({getTypeLabel(interview.type_entretien)})
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* History Dialog */}
      <Dialog open={!!selectedHistory} onOpenChange={(open) => { if (!open) { setSelectedHistory(null); setHistoryData([]); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Historique des entretiens - {selectedHistory?.employee_name}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-500 mb-4">
              Type: {selectedHistory && getTypeLabel(selectedHistory.type_entretien)}
            </p>
            {historyData.length === 0 ? (
              <p className="text-center text-slate-500 py-8">Aucun entretien précédent trouvé</p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {historyData.map((item, index) => (
                  <Card key={item.id} className={index === 0 ? 'border-[#0F5156]' : ''}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{formatDateShort(item.date_entretien)}</p>
                          <p className="text-sm text-slate-500">Évaluateur: {item.evaluateur}</p>
                          {item.heure_entretien && (
                            <p className="text-sm text-slate-500">{item.heure_entretien} - {item.duree_minutes || 60} min</p>
                          )}
                        </div>
                        <Badge className={getStatusColor(item.statut)}>
                          {getStatusLabel(item.statut)}
                        </Badge>
                      </div>
                      {item.bilan && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-sm font-medium">Bilan:</p>
                          <p className="text-sm text-slate-600">{item.bilan}</p>
                        </div>
                      )}
                      {item.points_forts && (
                        <div className="mt-2">
                          <p className="text-sm font-medium text-emerald-600">Points forts:</p>
                          <p className="text-sm text-slate-600">{item.points_forts}</p>
                        </div>
                      )}
                      {item.axes_amelioration && (
                        <div className="mt-2">
                          <p className="text-sm font-medium text-orange-600">Axes d'amélioration:</p>
                          <p className="text-sm text-slate-600">{item.axes_amelioration}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Interviews;
